import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../AuthContext';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import {momentLocalizer} from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarPage.css';
import AddActivityModal from '../addActivityModal/AddActivityModal';
import AddClientModal from '../addClientModal/AddClientModal';
import axios from 'axios';
import {USER_ENDPOINTS} from '../../Constants';
import AttachmentService from "../../services/AttachmentService";
import 'moment/locale/ro';
import {FiPlus} from "react-icons/fi";

moment.locale('ro');
const localizer = momentLocalizer(moment);

function CalendarPage() {
    const {isAuthenticated} = useAuth();
    const navigate = useNavigate();
    const [eventsData, setEventsData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [customMonthView, setCustomMonthView] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [showActivityModal, setShowActivityModal] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedEventData, setSelectedEventData] = useState(null);
    const [selectedPersonalEvent, setSelectedPersonalEvent] = useState(null);
    const [customerPhotos, setCustomerPhotos] = useState(new Map());
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    const [swipedEventId, setSwipedEventId] = useState(null);
    const [touchStartXEvent, setTouchStartXEvent] = useState(0);
    const [touchEndXEvent, setTouchEndXEvent] = useState(0);
    const [swipeOffset, setSwipeOffset] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null)

    const handleTouchStart = (e) => {
        touchStartX.current = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
        touchEndX.current = e.changedTouches[0].screenX;
        handleSwipeGesture();
    };
    const handleMouseEnter = (eventId) => {
        // Only on desktop
        if (window.innerWidth > 768) {
            setSwipedEventId(eventId);
            setSwipeOffset(prev => ({
                ...prev,
                [eventId]: 80
            }));
        }
    };

    const handleMouseLeave = (eventId) => {
        // Only on desktop
        if (window.innerWidth > 768) {
            setSwipedEventId(null);
            setSwipeOffset(prev => ({
                ...prev,
                [eventId]: 0
            }));
        }
    };

    const handleDeleteClick = (event) => {
        setEventToDelete(event);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!eventToDelete) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${USER_ENDPOINTS.DELETE_EVENT}/${eventToDelete.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Remove from local state
            setEventsData(prev => prev.filter(event => event.id !== eventToDelete.id));
            setSelectedEvents(prev => prev.filter(event => event.id !== eventToDelete.id));

            // Reset states
            setSwipedEventId(null);
            setSwipeOffset({});
            setShowDeleteConfirm(false);
            setEventToDelete(null);

            // Refresh events
            fetchEvents(currentMonth);
        } catch (error) {
            console.error('Error deleting event:', error);
            alert('Nu s-a putut șterge evenimentul');
        }
    };

    const handleSwipeGesture = () => {
        if (!touchStartX.current || !touchEndX.current) return;

        const deltaX = touchStartX.current - touchEndX.current;

        // Threshold to avoid accidental swipes
        if (Math.abs(deltaX) > 50) {
            if (deltaX > 0) {
                handleNavigate('NEXT'); // swipe left -> next month
            } else {
                handleNavigate('PREV'); // swipe right -> previous month
            }
        }

        // Reset
        touchStartX.current = null;
        touchEndX.current = null;
    };

    const handleEventTouchStart = (e, eventId) => {
        e.stopPropagation();
        setTouchStartXEvent(e.touches[0].clientX);
    };

    const handleEventTouchMove = (e, eventId) => {
        e.stopPropagation();
        const currentX = e.touches[0].clientX;
        const diff = touchStartXEvent - currentX;

        // Only allow left swipe (positive diff)
        if (diff > 0) {
            setSwipeOffset(prev => ({
                ...prev,
                [eventId]: Math.min(diff, 100) // Max swipe of 100px
            }));
        }
    };

    const handleEventTouchEnd = (e, eventId) => {
        e.stopPropagation();
        const offset = swipeOffset[eventId] || 0;

        if (offset > 50) { // Threshold to show delete button
            setSwipedEventId(eventId);
            setSwipeOffset(prev => ({
                ...prev,
                [eventId]: 80 // Fixed position for delete button
            }));
        } else {
            // Reset if not swiped enough
            setSwipeOffset(prev => ({
                ...prev,
                [eventId]: 0
            }));
            setSwipedEventId(null);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${USER_ENDPOINTS.DELETE_EVENT}/${eventId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Remove from local state
            setEventsData(prev => prev.filter(event => event.id !== eventId));
            setSelectedEvents(prev => prev.filter(event => event.id !== eventId));

            // Reset swipe states
            setSwipedEventId(null);
            setSwipeOffset({});

            // Refresh events
            fetchEvents(currentMonth);
        } catch (error) {
            console.error('Error deleting event:', error);
            alert('Nu s-a putut șterge evenimentul');
        }
    };

    const resetSwipe = (eventId) => {
        setSwipeOffset(prev => ({
            ...prev,
            [eventId]: 0
        }));
        setSwipedEventId(null);
    };

    // Function to fetch events for the current month
    const fetchEvents = useCallback(async (month = currentMonth) => {
        try {
            setLoading(true);
            setError(null);

            // Calculate the visible range of the calendar grid (includes overflow days from adjacent months)
            const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
            const lastDayOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);

            // Start from the Monday of the week containing the 1st
            const dayOfWeek = firstDayOfMonth.getDay();
            const startDate = new Date(firstDayOfMonth);
            startDate.setDate(firstDayOfMonth.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));

            // End at the Sunday of the last week shown (6 weeks max)
            const totalDays = Math.ceil((lastDayOfMonth.getDate() - firstDayOfMonth.getDate() + 1) / 7) * 7 + 7;
            const weeksToDisplay = Math.ceil(totalDays / 7);
            const endDate = new Date(startDate);
            endDate.setDate(startDate.getDate() + (weeksToDisplay * 7) - 1);

            // Format dates for API using year, month, day to avoid timezone issues
            const formattedStartDate = `${startDate.getFullYear()}-${(startDate.getMonth() + 1).toString().padStart(2, '0')}-${startDate.getDate().toString().padStart(2, '0')}`;
            const formattedEndDate = `${endDate.getFullYear()}-${(endDate.getMonth() + 1).toString().padStart(2, '0')}-${endDate.getDate().toString().padStart(2, '0')}`;

            console.log(`Fetching events from ${formattedStartDate} to ${formattedEndDate}`);

            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(USER_ENDPOINTS.GET_EVENTS, {
                params: {
                    startDate: formattedStartDate,
                    endDate: formattedEndDate
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            // Transform API events to calendar events format
            const apiEvents = response.data.map(event => {
                const startTime = event.startTime ? event.startTime.substring(0, 5) : '00:00';
                const endTime = event.endTime ? event.endTime.substring(0, 5) : '00:00';

                let formattedTitle;
                if (event.customerEntity) {
                    const clientName = `${event.customerEntity.lastName} ${event.customerEntity.firstName}`;

                    // Create a temporary event object to determine the measurement text
                    const tempEvent = {
                        rawData: event,
                        eventType: event.eventType,
                        isPersonalEvent: !event.customerEntity
                    };

                    // Get the appropriate text for measurements
                    const measurementText = getMeasurementDisplayText(tempEvent);

                    // Use custom text for measurements, otherwise use description
                    const displayText = measurementText || event.description || 'Fără descriere';
                    formattedTitle = `${clientName} - ${displayText}`;
                } else {
                    const description = event.description || 'Fără descriere';
                    formattedTitle = `${description} (Personal)`;
                }

                // Create a temporary event object for color calculation
                const tempEvent = {
                    rawData: event,
                    eventType: event.eventType,
                    isPersonalEvent: !event.customerEntity,
                    color: event.color
                };

                return {
                    id: event.id,
                    title: formattedTitle,
                    start: new Date(`${event.scheduledDate}T${event.startTime || '00:00:00'}`),
                    end: new Date(`${event.scheduledDate}T${event.endTime || '00:00:00'}`),
                    color: event.color || getEventColor(tempEvent),
                    desc: event.additionalInfo,
                    apiEvent: true,
                    rawData: event,
                    customerId: event.customerEntity?.id,
                    eventType: event.eventType,
                    isPersonalEvent: !event.customerEntity,
                    hasPhotos: false // Initialize as false, will be updated later
                };
            });

            setEventsData(apiEvents);

            if (selectedDate) {
                const eventsOnSelectedDate = apiEvents.filter(event => {
                    const eventDate = new Date(event.start);
                    return eventDate.toDateString() === selectedDate.toDateString();
                });
                setSelectedEvents(eventsOnSelectedDate);
            }

        } catch (err) {
            console.error('Error fetching events:', err);
            setError('Nu s-au putut încărca evenimentele. Vă rugăm să încercați din nou.');
        } finally {
            setLoading(false);
        }
    }, [currentMonth, selectedDate]);

    useEffect(() => {
        const loadPhotosForVisibleEvents = async () => {
            // Get unique customer IDs from current events that don't have photos checked yet
            const customerIds = [...new Set(eventsData
                .filter(event => event.customerId && !customerPhotos.has(event.customerId))
                .map(event => event.customerId)
            )];

            if (customerIds.length === 0) return; // Exit early if no new customers

            // Check photos for each customer
            for (const customerId of customerIds) {
                try {
                    const hasPhotos = await checkEventPhotos(customerId);

                    // Update only the events for this specific customer
                    const updatePhotos = event =>
                        event.customerId === customerId ? {...event, hasPhotos} : event;
                    setEventsData(prevEvents => prevEvents.map(updatePhotos));
                    setSelectedEvents(prevEvents => prevEvents.map(updatePhotos));
                } catch (error) {
                    console.error(`Error loading photos for customer ${customerId}:`, error);
                }
            }
        };

        if (eventsData.length > 0) {
            loadPhotosForVisibleEvents();
        }
    }, [eventsData.length]);

    // Fetch events when component mounts or month changes
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
        } else {
            fetchEvents(currentMonth);
        }
    }, [isAuthenticated, navigate, currentMonth]);

    // Function to fetch client data by ID
    const fetchClientData = async (clientId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Fetch client data
            const response = await axios.get(`${USER_ENDPOINTS.GET_CUSTOMER}/${clientId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching client data:', error);
            return null;
        }
    };

    const checkEventPhotos = async (customerId) => {
        if (!customerId) return false;

        // Check cache first
        if (customerPhotos.has(customerId)) {
            return customerPhotos.get(customerId);
        }

        try {
            const photoAttachments = await AttachmentService.getAttachments(customerId, 'PHOTO');
            const hasPhotos = photoAttachments && photoAttachments.length > 0;

            // Cache the result
            setCustomerPhotos(prev => new Map(prev).set(customerId, hasPhotos));
            return hasPhotos;
        } catch (error) {
            console.error('Error checking photo attachments:', error);
            return false;
        }
    };

    const capitalizeFirstLetter = (month) => {
        return month.charAt(0).toUpperCase() + month.slice(1);
    }

    // Function to fetch client events by event type
    const fetchClientEvents = async (clientId, eventType) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            // Fetch client events
            const response = await axios.get(`${USER_ENDPOINTS.GET_EVENTS}/${clientId}`, {
                params: {
                    eventType: eventType
                },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return response.data;
        } catch (error) {
            console.error(`Error fetching client events of type ${eventType}:`, error);
            return [];
        }
    };

    // Handle adding a new activity
    const handleAddActivity = async (activityData) => {
        // Process the time strings into proper Date objects
        const startDate = new Date(selectedDate || new Date());
        const [startHours, startMinutes] = activityData.start.split(':').map(Number);
        startDate.setHours(startHours, startMinutes, 0);

        const endDate = new Date(selectedDate || new Date());
        const [endHours, endMinutes] = activityData.end.split(':').map(Number);
        endDate.setHours(endHours, endMinutes, 0);

        // Create new event (this is just for UI, actual data saved via API in AddActivityModal)
        const newEvent = {
            id: Date.now(), // Temporary ID until we refresh from API
            title: activityData.title,
            start: startDate,
            end: endDate,
            isPersonalEvent: true,
            color: activityData.color // Store the color for later use
        };

        // Add event to the UI optimistically
        setEventsData(prev => [...prev, newEvent]);
        setSelectedEvents(prev => [...prev, newEvent]);

        // Refresh events data from server after a brief delay to ensure API has processed the new event
        setTimeout(() => {
            fetchEvents(currentMonth);
        }, 1000);

        // Reset selected personal event
        setSelectedPersonalEvent(null);
    };

    // Refresh photo icon for a customer after photos may have changed
    const refreshCustomerPhotos = async (customerId) => {
        if (!customerId) return;
        AttachmentService.clearCache(customerId, 'PHOTO');
        try {
            const photoAttachments = await AttachmentService.getAttachments(customerId, 'PHOTO', true);
            const hasPhotos = photoAttachments && photoAttachments.length > 0;
            setCustomerPhotos(prev => new Map(prev).set(customerId, hasPhotos));
            const updatePhotos = event =>
                event.customerId === customerId ? {...event, hasPhotos} : event;
            setEventsData(prevEvents => prevEvents.map(updatePhotos));
            setSelectedEvents(prevEvents => prevEvents.map(updatePhotos));
        } catch (error) {
            console.error('Error refreshing photos:', error);
        }
    };

    // Handle adding a client with measurements and installations
    const handleAddClient = async (clientData) => {
        console.log('Client adăugat:', clientData);

        // Refresh calendar events to show any newly created measurements and installations
        await fetchEvents(currentMonth);

        // Re-check photos for this customer so the icon updates without refresh
        if (clientData?.id) {
            await refreshCustomerPhotos(clientData.id);
        }

        // Close the modal after handling the client data
        setShowClientModal(false);
        setSelectedClient(null);
        setSelectedEventData(null);
    };

    // Handle event click to open client details or personal event
    const handleEventClick = async (event) => {
        console.log('Event clicked:', event);

        // Handle personal event
        if (event.isPersonalEvent) {
            // Set the selected personal event and open the activity modal
            setSelectedPersonalEvent({
                id: event.id,
                title: event.rawData.description,
                start: event.rawData.startTime.substring(0, 5),
                end: event.rawData.endTime.substring(0, 5),
                scheduledDate: event.rawData.scheduledDate,
                color: event.rawData.color || '#808080'
            });
            setShowActivityModal(true);
            return;
        }

        // Handle client event
        if (event.customerId) {
            try {
                setLoading(true);

                // Fetch client data
                const clientData = await fetchClientData(event.customerId);
                console.log('Raw client data from API:', clientData);

                if (clientData) {
                    // Fetch associated events (measurements and installations)
                    const measurementEvents = await fetchClientEvents(event.customerId, 'MASURATORI');
                    const installationEvents = await fetchClientEvents(event.customerId, 'MONTAJ');

                    // Transform the measurements data
                    const measurements = measurementEvents.map(event => ({
                        id: event.id,
                        date: event.scheduledDate,
                        startTime: event.startTime ? event.startTime.substring(0, 5) : '09:00',
                        endTime: event.endTime ? event.endTime.substring(0, 5) : '10:00',
                        description: event.description || 'Măsurătoare',
                        eventType: 'MASURATORI'
                    }));

                    // Transform the installations data
                    const installations = installationEvents.map(event => ({
                        id: event.id,
                        date: event.scheduledDate,
                        startTime: event.startTime ? event.startTime.substring(0, 5) : '09:00',
                        endTime: event.endTime ? event.endTime.substring(0, 5) : '10:00',
                        description: event.description || 'Montaj',
                        eventType: 'MONTAJ'
                    }));

                    // Debug log original client type
                    console.log('Original client type from API:', clientData.clientType);

                    // Don't modify the clientType field here, just pass it through
                    // The AddClientModal will handle the mapping from API to UI format
                    const formattedClientData = {
                        id: clientData.id,
                        firstName: clientData.firstName || '',
                        lastName: clientData.lastName || '',
                        email: clientData.email || '',
                        phone1: clientData.phone1 || '',
                        phone2: clientData.phone2 || '',
                        address: clientData.homeAddress || '',
                        furnitureType: clientData.furnitureType || '',
                        cnp: clientData.cnp || '',
                        idSeries: clientData.serialNumber || '',
                        details: clientData.details || '',
                        advance: clientData.advance || (clientData.initialPayment ? clientData.initialPayment.toString() : ''),
                        total: clientData.total || (clientData.totalAmount ? clientData.totalAmount.toString() : ''),
                        rest: clientData.totalAmount && clientData.initialPayment
                            ? (clientData.totalAmount - clientData.initialPayment).toString()
                            : '',

                        // Pass through the exact API clientType value
                        clientType: clientData.clientType,

                        // Pass through company fields
                        companyName: clientData.companyName || '',
                        cui: clientData.cui || '',
                        companyAddress: clientData.companyAddress || '',

                        contract: getContractType(clientData.activity),
                        measurements: measurements,
                        installations: installations,

                        initialPaymentType: clientData.initialPaymentType, // Backend payment type
                        remainingPaymentType: clientData.remainingPaymentType, // Backend payment type
                        isRemainingPaymentPaid: clientData.isRemainingPaymentPaid // Backend payment status
                    };

                    console.log('Formatted client data for modal:', formattedClientData);

                    // Set the selected client and open the modal
                    setSelectedClient(formattedClientData);
                    setSelectedEventData(event.rawData);
                    setShowClientModal(true);
                }
            } catch (error) {
                console.error('Error preparing client data:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Helper function to map contract type from API format to display format
    const getContractType = (activityType) => {
        switch (activityType) {
            case 'CONTRACT':
            case 'CONTRACT_NESEMNAT':
                return 'Contract nesemnat';
            case 'CONTRACT_SEMNAT':
                return 'Contract semnat';
            case 'IN_OFERTARE':
                return 'In ofertare';
            case 'FARA_CONTRACT':
                return 'Fara contract';
            default:
                return '';
        }
    };

    // Get the start of the week for a given date
    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
        return new Date(d.setDate(diff));
    };

    // Get all days in a week
    const getDaysInWeek = (startDate) => {
        const days = [];
        const currentDate = new Date(startDate);

        for (let i = 0; i < 7; i++) {
            days.push(new Date(currentDate));
            currentDate.setDate(currentDate.getDate() + 1);
        }

        return days;
    };

// Înlocuiește funcția handleSelectSlot existentă cu aceasta:
    const handleSelectSlot = (slotInfo) => {
        // When a user selects a day
        const selectedDate = new Date(slotInfo.start);

        // Ensure we're working with the day in local time zone
        const localDate = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            12, 0, 0 // Set to noon to avoid any timezone boundary issues
        );

        console.log('Selected date:', localDate.toISOString()); // Debug log

        // Check if clicking on the same date while list is open - toggle close
        if (selectedDate && customMonthView &&
            localDate.toDateString() === selectedDate.toDateString()) {
            closeEventsList();
            return;
        }

        setSelectedDate(localDate);

        // Find events for the selected date
        const eventsOnDay = eventsData.filter(event => {
            const eventDate = new Date(event.start);
            return eventDate.toDateString() === localDate.toDateString();
        });

        setSelectedEvents(eventsOnDay);
        setCustomMonthView(true);
    };

    const handleNavigate = (action) => {
        let newMonth = new Date(currentMonth);

        if (action === 'PREV') {
            newMonth.setMonth(newMonth.getMonth() - 1);
        } else if (action === 'NEXT') {
            newMonth.setMonth(newMonth.getMonth() + 1);
        } else if (action === 'TODAY') {
            newMonth = new Date();
        }

        setCurrentMonth(newMonth);
        // Clear selection when navigating to different month
        setSelectedDate(null);
        setCustomMonthView(false);
    };

    // Close the event list view and return to normal month view
    const closeEventsList = () => {
        setSelectedDate(null);
        setCustomMonthView(false);
    };

    // Function to format time from Date object
    const formatTime = (date) => {
        return date ? moment(date).format('HH:mm') : '';
    };

    // Render the standard month view (without events list)
    const renderMonthView = () => {
        // Get the first day of the month
        const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

        // Calculate the start of the first week of the month (might be in the previous month)
        const startOfFirstWeek = getWeekStart(firstDayOfMonth);

        // Get the last day of the month
        const lastDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

        // Calculate how many weeks to display (usually 5 or 6)
        const totalDays = Math.ceil((lastDayOfMonth.getDate() - firstDayOfMonth.getDate() + 1) / 7) * 7 + 7; // Add buffer
        const weeksToDisplay = Math.ceil(totalDays / 7);

        // Generate all the weeks for the month
        const monthWeeks = [];
        for (let i = 0; i < weeksToDisplay; i++) {
            const weekStartDate = new Date(startOfFirstWeek);
            weekStartDate.setDate(startOfFirstWeek.getDate() + (i * 7));
            monthWeeks.push(getDaysInWeek(weekStartDate));
        }

        if (loading) {
            return (
                <div className="loading-container">
                    <div className="loading-indicator">Se încarcă evenimentele...</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="error-container">
                    <div className="error-message">{error}</div>
                    <button className="retry-button" onClick={() => fetchEvents(currentMonth)}>
                        Încearcă din nou
                    </button>
                </div>
            );
        }

        return (
            <div className="custom-month-view"
                 onTouchStart={handleTouchStart}
                 onTouchEnd={handleTouchEnd}>
                {/* Week header (Mon, Tue, etc.) */}
                <div className="week-header">
                    {['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'].map((day, i) => (
                        <div key={i} className="week-day-header">{day}</div>
                    ))}
                </div>

                {/* All weeks of the month */}
                {monthWeeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="week-row">
                        {week.map((day, dayIndex) => {
                            // Get events for this day
                            const dayEvents = eventsData.filter(event => {
                                const eventDate = new Date(event.start);
                                return eventDate.toDateString() === day.toDateString();
                            });

                            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                            const today = new Date();
                            const isToday = day.toDateString() === today.toDateString();
                            const isPastDay = day < today && !isToday;
                            const dayClasses = ['day-cell'];
                            if (!isCurrentMonth) {
                                dayClasses.push('other-month');
                                // Only add past-day if the day is actually in the past
                                if (isPastDay) {
                                    dayClasses.push('past-day');
                                }
                            } else {
                                if (isToday) dayClasses.push('today');
                                if (isPastDay) dayClasses.push('past-day');
                            }

                            return (
                                <div
                                    key={dayIndex}
                                    className={dayClasses.join(' ')}
                                    onClick={() => {
                                        // Check if clicking on the same date while list is open - toggle close
                                        if (selectedDate && customMonthView &&
                                            day.toDateString() === selectedDate.toDateString()) {
                                            closeEventsList();
                                            return;
                                        }

                                        setSelectedDate(day);
                                        setSelectedEvents(dayEvents);
                                        setCustomMonthView(true);
                                    }}
                                >
                                    <div className="day-number">{day.getDate()}</div>
                                    {dayEvents.length > 0 && (
                                        <div className="day-events-preview">
                                            {dayEvents.slice(0, 3).map((event, idx) => (
                                                <div
                                                    key={idx}
                                                    className="event-preview"
                                                    style={{borderColor: event.color}}
                                                >
                                                    <span>{event.title}</span>
                                                </div>
                                            ))}
                                            {dayEvents.length > 3 && (
                                                <div className="more-events">+{dayEvents.length - 3} mai multe</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    // Generate a custom calendar view with events list
    const renderCustomCalendarView = () => {
        if (!selectedDate || !customMonthView) return null;

        // Get the first day of the month
        const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);

        // Calculate the start of the first week of the month (might be in the previous month)
        const startOfFirstWeek = getWeekStart(firstDayOfMonth);

        // Get the last day of the month
        const lastDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

        // Calculate how many weeks to display (usually 5 or 6)
        const totalDays = Math.ceil((lastDayOfMonth.getDate() - firstDayOfMonth.getDate() + 1) / 7) * 7 + 7; // Add buffer
        const weeksToDisplay = Math.ceil(totalDays / 7);

        // Generate all the weeks for the month
        const monthWeeks = [];
        for (let i = 0; i < weeksToDisplay; i++) {
            const weekStartDate = new Date(startOfFirstWeek);
            weekStartDate.setDate(startOfFirstWeek.getDate() + (i * 7));
            monthWeeks.push(getDaysInWeek(weekStartDate));
        }

        // Find the week number (index) that contains the selected date
        const selectedWeekNumber = monthWeeks.findIndex(week =>
            week.some(day => day.toDateString() === selectedDate.toDateString())
        );

        return (
            <div className="custom-month-view"
                 onTouchStart={handleTouchStart}
                 onTouchEnd={handleTouchEnd}>
                {/* Week header (Mon, Tue, etc.) */}
                <div className="week-header">
                    {['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'].map((day, i) => (
                        <div key={i} className="week-day-header">{day}</div>
                    ))}
                </div>

                {/* Weeks before the selected week */}
                {monthWeeks.slice(0, selectedWeekNumber + 1).map((week, weekIndex) => (
                    <div key={weekIndex}
                         className={`week-row ${weekIndex === selectedWeekNumber ? 'selected-week' : ''}`}>
                        {week.map((day, dayIndex) => {
                            // Get events for this day
                            const dayEvents = eventsData.filter(event => {
                                const eventDate = new Date(event.start);
                                return eventDate.toDateString() === day.toDateString();
                            });

                            const isSelectedDay = day.toDateString() === selectedDate.toDateString();
                            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
                            const today = new Date();
                            const isToday = day.toDateString() === today.toDateString();
                            const isPastDay = day < today && !isToday;
                            const dayClasses = ['day-cell'];
                            if (!isCurrentMonth) {
                                dayClasses.push('other-month');
                                // Only add past-day if the day is actually in the past
                                if (isPastDay) {
                                    dayClasses.push('past-day');
                                }
                            } else {
                                if (isToday) dayClasses.push('today');
                                if (isPastDay) dayClasses.push('past-day');
                            }
                            return (
                                <div
                                    key={dayIndex}
                                    className={dayClasses.join(' ')}
                                    onClick={() => {
                                        // Check if clicking on the same date while list is open - toggle close
                                        if (selectedDate && customMonthView &&
                                            day.toDateString() === selectedDate.toDateString()) {
                                            closeEventsList();
                                            return;
                                        }

                                        setSelectedDate(day);
                                        setSelectedEvents(dayEvents);
                                    }}
                                >
                                    <div className="day-number">{day.getDate()}</div>
                                    {dayEvents.length > 0 && (
                                        <div className="day-events-preview">
                                            {dayEvents.slice(0, 3).map((event, idx) => (
                                                <div
                                                    key={idx}
                                                    className="event-preview"
                                                    style={{borderColor: event.color}}
                                                >
                                                    <span>{event.title}</span>
                                                </div>
                                            ))}
                                            {dayEvents.length > 3 && (
                                                <div className="more-events">+{dayEvents.length - 3} mai multe</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}

                {/* Events list for selected day */}
                <div className="selected-day-events">
                    <div className="events-header">
                        <h2>{moment(selectedDate).format('DD MMMM YYYY')}</h2>
                        <button className="close-events-btn" onClick={closeEventsList}>×</button>
                    </div>

                    {loading ? (
                        <div className="events-loading">
                            <div className="loading-indicator">Se încarcă evenimentele...</div>
                        </div>
                    ) : error ? (
                        <div className="events-error">
                            <div className="error-message">{error}</div>
                            <button className="retry-button" onClick={() => fetchEvents(currentMonth)}>
                                Încearcă din nou
                            </button>
                        </div>
                    ) : selectedEvents.length > 0 ? (
                        <div className="events-list">
                            {selectedEvents
                                .sort((a, b) => new Date(a.start) - new Date(b.start))
                                .map((event, index) => (
                                    <div
                                        key={index}
                                        className="event-card-wrapper"
                                        onMouseEnter={() => handleMouseEnter(event.id)}
                                        onMouseLeave={() => handleMouseLeave(event.id)}
                                    >
                                        <div
                                            className="event-card"
                                            style={{
                                                borderColor: event.color || getEventColor(index),
                                                borderLeftWidth: '4px',
                                                borderLeftStyle: 'solid',
                                                transform: `translateX(-${swipeOffset[event.id] || 0}px)`,
                                                transition: swipeOffset[event.id] === 80 || swipeOffset[event.id] === 0 ? 'transform 0.3s ease' : 'none'
                                            }}
                                            onClick={() => {
                                                if (swipedEventId === event.id && window.innerWidth <= 768) {
                                                    resetSwipe(event.id);
                                                } else {
                                                    handleEventClick(event);
                                                }
                                            }}
                                            onTouchStart={(e) => handleEventTouchStart(e, event.id)}
                                            onTouchMove={(e) => handleEventTouchMove(e, event.id)}
                                            onTouchEnd={(e) => handleEventTouchEnd(e, event.id)}
                                        >
                                            {/* Layout pentru desktop - structura originală */}
                                            <div className="desktop-layout">
                                                <div className="event-time">
                                                    {formatTime(event.start)} - {formatTime(event.end)}
                                                </div>
                                                <div className="event-content">
                                                    <div className="event-title">
                                                        {event.title}
                                                    </div>
                                                    {event.desc && (
                                                        <div className="event-description">
                                                            {event.desc}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="event-icons">
                                                    {/* Event type icons */}
                                                    {event.eventType === 'MONTAJ' &&
                                                        <span className="event-icon">🔧</span>}
                                                    {event.eventType === 'MASURATORI' && (
                                                        <span
                                                            className="event-icon"
                                                            title={getMeasurementDisplayText(event) || 'Măsurătoare'}
                                                        >
                {getMeasurementIcon(event)}
            </span>
                                                    )}
                                                    {event.isPersonalEvent && <span className="event-icon">👤</span>}
                                                    {event.customerId && <span className="event-icon">👥</span>}
                                                    {event.hasPhotos && <span className="event-icon">📸</span>}
                                                </div>
                                            </div>

                                            {/* Layout pentru mobil - structura nouă */}
                                            <div className="mobile-layout">
                                                <div className="mobile-time-icons-row">
                                                    <div className="event-time">
                                                        {formatTime(event.start)} - {formatTime(event.end)}
                                                    </div>
                                                    <div className="event-icons">
                                                        {/* Event type icons */}
                                                        {event.eventType === 'MONTAJ' &&
                                                            <span className="event-icon">🔧</span>}
                                                        {event.eventType === 'MASURATORI' && (
                                                            <span
                                                                className="event-icon"
                                                                title={getMeasurementDisplayText(event) || 'Măsurătoare'}
                                                            >
                    {getMeasurementIcon(event)}
                </span>
                                                        )}
                                                        {event.isPersonalEvent && <span className="event-icon">👤</span>}
                                                        {event.customerId && <span className="event-icon">👥</span>}
                                                        {event.hasPhotos && <span className="event-icon">📸</span>}
                                                    </div>
                                                </div>
                                                <div className="event-content">
                                                    <div className="event-title">
                                                        {event.title}
                                                    </div>
                                                    {event.desc && (
                                                        <div className="event-description">
                                                            {event.desc}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Delete button */}
                                        <button
                                            className={`delete-btn ${swipedEventId === event.id ? 'visible' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteClick(event);
                                            }}
                                        >
                                            Șterge
                                        </button>
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <p className="no-events">Nu există evenimente programate pentru această zi.</p>
                    )}
                    <div className="add-event-container">
                        <button className="add-event-btn" onClick={() => {
                            setSelectedClient(null);
                            setSelectedEventData(null);
                            setShowClientModal(true);
                        }}>
                            Adaugă client
                        </button>
                        <button
                            className="add-client-btn"
                            onClick={() => {
                                setSelectedPersonalEvent(null);
                                setShowActivityModal(true);
                            }}
                            aria-label="Add new item"
                        >
                            <FiPlus/>
                        </button>
                    </div>
                </div>

                {/* Weeks after the selected week */}
                {monthWeeks.slice(selectedWeekNumber + 1).map((week, weekIndex) => (
                    <div key={weekIndex} className="week-row">
                        {week.map((day, dayIndex) => {
                            // Get events for this day
                            const dayEvents = eventsData.filter(event => {
                                const eventDate = new Date(event.start);
                                return eventDate.toDateString() === day.toDateString();
                            });

                            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
                            const today = new Date();
                            const isToday = day.toDateString() === today.toDateString();
                            const isPastDay = day < today && !isToday;
                            const dayClasses = ['day-cell'];
                            if (!isCurrentMonth) {
                                dayClasses.push('other-month');
                                // Only add past-day if the day is actually in the past
                                if (isPastDay) {
                                    dayClasses.push('past-day');
                                }
                            } else {
                                if (isToday) dayClasses.push('today');
                                if (isPastDay) dayClasses.push('past-day');
                            }
                            return (
                                <div
                                    key={dayIndex}
                                    className={dayClasses.join(' ')}
                                    onClick={() => {
                                        setSelectedDate(day);
                                        setSelectedEvents(dayEvents);
                                    }}
                                >
                                    <div className="day-number">{day.getDate()}</div>
                                    {dayEvents.length > 0 && (
                                        <div className="day-events-preview">
                                            {dayEvents.slice(0, 2).map((event, idx) => (
                                                <div
                                                    key={idx}
                                                    className="event-preview"
                                                    style={{borderColor: event.color}}
                                                >
                                                    <span>{event.title}</span>
                                                </div>
                                            ))}
                                            {dayEvents.length > 2 && (
                                                <div className="more-events">+{dayEvents.length - 2} mai multe</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <AuthenticatedLayout>
            <div className="calendar-container">
                <div className="header-container">
                    <h1 className="page-title">Calendar</h1>
                </div>

                {/* Always use our custom calendar view */}
                <div className="calendar-controls"
                     onTouchStart={handleTouchStart}
                     onTouchEnd={handleTouchEnd}>
                    <button className="nav-btn" onClick={() => handleNavigate('TODAY')}>Azi - {moment().format('DD.MM.YYYY')}</button>
                    <div className="month-navigation">
                        <button className="nav-btn" onClick={() => handleNavigate('PREV')}>←</button>
                        <h2>{capitalizeFirstLetter(moment(currentMonth).format('MMMM YYYY'))}</h2>
                        <button className="nav-btn" onClick={() => handleNavigate('NEXT')}>→</button>
                    </div>
                </div>

                {customMonthView && selectedDate ? (
                    // Render the calendar with events list
                    renderCustomCalendarView()
                ) : (
                    // Render standard month view without events list
                    renderMonthView()
                )}

                {/* Activity Modal */}
                <AddActivityModal
                    isOpen={showActivityModal}
                    onClose={() => {
                        setShowActivityModal(false);
                        setSelectedPersonalEvent(null);
                    }}
                    onSave={handleAddActivity}
                    selectedDate={selectedDate}
                    eventToEdit={selectedPersonalEvent}
                />

                {/* Client Modal */}
                <AddClientModal
                    isOpen={showClientModal}
                    onClose={() => {
                        // Re-check photos in case user uploaded photos via auto-save gallery
                        if (selectedClient?.id) {
                            refreshCustomerPhotos(selectedClient.id);
                        }
                        setShowClientModal(false);
                        setSelectedClient(null);  // Add this line to reset selected client
                    }}
                    onSave={handleAddClient}
                    initialData={selectedClient}
                />
            </div>
            {showDeleteConfirm && (
                <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close-btn"
                            onClick={() => {
                                setShowDeleteConfirm(false);
                                setEventToDelete(null);
                            }}
                        >
                            ×
                        </button>
                        <div className="modal-body">
                            <p className="delete-question">
                                Sigur doriți să ștergeți evenimentul <strong>{eventToDelete?.title}</strong>?
                            </p>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="confirm-yes-btn"
                                onClick={confirmDelete}
                            >
                                Da
                            </button>
                            <button
                                className="confirm-no-btn"
                                onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setEventToDelete(null);
                                }}
                            >
                                Nu
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AuthenticatedLayout>
    );
}

/// Helper function to get colors for events based on type and payment status
function getEventColor(event) {
    // If event already has a color defined, use it (for personal events with custom colors)
    if (event.color && event.isPersonalEvent) return event.color;

    // For personal events without custom color
    if (event.isPersonalEvent) return '#808080'; // Gri

    // For client events, determine color based on event type and payment status
    if (event.rawData && event.rawData.customerEntity) {
        const customer = event.rawData.customerEntity;
        const eventType = event.rawData.eventType;

        // Check if remaining payment is paid (client fully paid)
        const isFullyPaid = customer.isRemainingPaymentPaid;

        // Check if client has initial payment (advance)
        const hasAdvance = customer.initialPayment && parseFloat(customer.initialPayment) > 0;

        // If fully paid, everything becomes green
        if (isFullyPaid) {
            return '#28a745'; // Verde - fully paid
        }

        // For measurements (MASURATORI)
        if (eventType === 'MASURATORI') {
            // If there's an advance payment (regardless of total), measurements are red
            if (hasAdvance) {
                return '#dc3545'; // Roșu - advance paid but not fully paid
            }
            // If no advance, use default measurement color
            return '#FFD700'; // Galben - default measurement color
        }

        // For installations (MONTAJ)
        if (eventType === 'MONTAJ') {
            // Installations keep their default color unless fully paid
            return '#4169E1'; // Albastru - default installation color
        }
    }

    // Fallback to event type based colors for events without customer data
    if (event.eventType) {
        switch (event.eventType) {
            case 'MASURATORI':
                return '#FFD700'; // Galben
            case 'MONTAJ':
                return '#4169E1'; // Albastru
            default:
                return '#808080'; // Gri
        }
    }

    // Default fallback
    return '#808080'; // Gri
}

function getMeasurementDisplayText(event) {
    // Only apply to measurement events
    if (event.rawData?.eventType !== 'MASURATORI') {
        return null; // Return null for non-measurement events
    }

    if (event.rawData && event.rawData.customerEntity) {
        const customer = event.rawData.customerEntity;

        // Check if remaining payment is paid (client fully paid)
        const isFullyPaid = customer.isRemainingPaymentPaid;

        // Check if client has initial payment (advance)
        const hasAdvance = customer.initialPayment && parseFloat(customer.initialPayment) > 0;

        // If fully paid, show "Finalizat"
        if (isFullyPaid) {
            return 'Finalizat';
        }

        // If there's an advance payment but not fully paid, show "In lucru"
        if (hasAdvance) {
            return 'In lucru';
        }

        // Default case - no advance payment, keep original text
        return 'Măsurătoare';
    }

    // Fallback for events without customer data
    return 'Măsurătoare';
}

function getMeasurementIcon(event) {
    // Only apply to measurement events
    if (event.eventType !== 'MASURATORI') {
        return null; // Return null for non-measurement events
    }

    if (event.rawData && event.rawData.customerEntity) {
        const customer = event.rawData.customerEntity;

        // Check if remaining payment is paid (client fully paid)
        const isFullyPaid = customer.isRemainingPaymentPaid;

        // Check if client has initial payment (advance)
        const hasAdvance = customer.initialPayment && parseFloat(customer.initialPayment) > 0;

        // If fully paid, show applause icon
        if (isFullyPaid) {
            return '👏'; // Aplauze pentru finalizat
        }

        // If there's an advance payment but not fully paid, show tools icon
        if (hasAdvance) {
            return '🧰'; // Trusa de scule pentru "in lucru"
        }

        // Default case - no advance payment, show measurement icon
        return '📏'; // Măsurătoare standard
    }

    // Fallback for events without customer data
    return '📏';
}

export default CalendarPage;